import type { UseCookieProps } from '@/hooks/common';
import { CookieType } from './common';
import { DOMAINS, ONE_SIGNAL_APP_IDS } from './constants';
import { timestampByUTC } from './dateTime';

export const getCurrentHostname = (hostname: string) => {
  const isMirror2 = hostname.includes(DOMAINS.MIRROR_2);
  const isDevEnv = process.env.NEXT_PUBLIC_APP_ENV === 'development';
  if (isMirror2 || isDevEnv) return DOMAINS.MIRROR_2;
  return hostname;
};

export const getReactAppApiUrl = (hostname: string) => {
  const correctHostname = getCurrentHostname(hostname);
  return `https://www.${correctHostname}`;
};

export const REACT_APP_API_URL =
  typeof window !== 'undefined'
    ? getReactAppApiUrl(window.location.hostname)
    : '';

export const getPaymentsLibUrl = (hostname: string) =>
  `${getReactAppApiUrl(hostname)}/payments-lib/v1/index.js`;

export const getOneSignalId = (hostname: string) => {
  switch (hostname) {
    case DOMAINS.MIRROR_2:
      return ONE_SIGNAL_APP_IDS.MIRROR_2;
    case DOMAINS.MIRROR_8:
      return ONE_SIGNAL_APP_IDS.MIRROR_8;
    case DOMAINS.MIRROR_10:
      return ONE_SIGNAL_APP_IDS.MIRROR_10;
    case DOMAINS.MIRROR_30:
      return ONE_SIGNAL_APP_IDS.MIRROR_30;
    case DOMAINS.MIRROR_35:
      return ONE_SIGNAL_APP_IDS.MIRROR_35;
    case DOMAINS.MAIN:
      return ONE_SIGNAL_APP_IDS.MAIN;
    case DOMAINS.DEV:
    default:
      return;
  }
};
export const ONE_SIGNAL_ID =
  typeof window !== 'undefined' ? getOneSignalId(window.location.hostname) : '';

export enum Country {
  WorldWide = 'WW',
  // START Country for translations
  Germany = 'DE',
  Canada = 'CA',
  Switzerland = 'CH',
  Italy = 'IT',
  Norway = 'NO',
  Portugal = 'PT',
  Brazil = 'BR',
  Australia = 'AU',
  Ireland = 'IE',
  NewZealand = 'NZ',
  Denmark = 'DK',
  // END Country for translations
  Austria = 'AT',
  Russia = 'RU',
  Poland = 'PL',
  UnitedStates = 'US',
  UnitedKingdom = 'GB',
  Ukraine = 'UA',
  Slovakia = 'SK',
  Spain = 'ES',
  France = 'FR',
  Guadeloupe = 'GP',
  Martinique = 'MQ',
  FrenchGuiana = 'GF',
  Reunion = 'RE',
  FrenchPolynesia = 'PF',
  WallisAndFutuna = 'WF',
  NewCaledonia = 'NC',
  Netherlands = 'NL',
  Israel = 'IL',
  Lithuania = 'LT',
  Curacao = 'CW',
  Gibraltar = 'GI',
  Jersey = 'JE',
  Greece = 'GR',
  Belgium = 'BE',
  Hungary = 'HU',
  Slovenia = 'SI',
  CzechRepublic = 'CZ',
  Serbia = 'RS',
  Syria = 'SY',
  NorthKorea = 'KP',
  Iran = 'IR',
  Cuba = 'CU',
  Turkey = 'TR',
  Jamaica = 'JM',
  Japan = 'JP',
  Afghanistan = 'AF',
  Azerbaijan = 'AZ',
  Bangladesh = 'BD',
  Belarus = 'BY',
  Bulgaria = 'BG',
  Croatia = 'HR',
  Cyprus = 'CY',
  Estonia = 'EE',
  Finland = 'FI',
  Georgia = 'GE',
  India = 'IN',
  Latvia = 'LV',
  Macedonia = 'MK',
  Malaysia = 'MY',
  Moldova = 'MD',
  Romania = 'RO',
  Angola = 'AO',
  Albania = 'AL',
  Iraq = 'IQ',
  Uganda = 'UG',
  Pakistan = 'PK',
  Panama = 'PA',
  Lebanon = 'LB',
  Zimbabwe = 'ZW',
  Mauritius = 'MU',
  Nicaragua = 'NI',
  Yemen = 'YE',
  CentralAfricanRepublic = 'CF',
  Sudan = 'SD',
  Liberia = 'LR',
  CaymanIslands = 'KY',
  Somalia = 'SO',
  RepublicOfTheCongo = 'CG',
  DemocraticRepublicOfTheCongo = 'CD',
  Eritrea = 'ER',
  Haiti = 'HT',
  SierraLeone = 'SL',
  Ethiopia = 'ET',
  Myanmar = 'MM',
  SouthSudan = 'SS',
  BurkinaFaso = 'BF',
  Libya = 'LY',
  Mali = 'ML',
  Barbados = 'BB',
  Rwanda = 'RW',
  AlandIslands = 'AX',
  NorfolkIsland = 'NF',
  Bahrain = 'BH',
  Mongolia = 'MN',
  Thailand = 'TH',
  HongKong = 'HK',
  Nigeria = 'NG',
  Antarctica = 'AQ',
  Tanzania = 'TZ',
  Tunisia = 'TN',
  Chad = 'TD',
  Honduras = 'HN',
  Andorra = 'AD',
  TrinidadAndTobago = 'TT',
  Jordan = 'JO',
  Egypt = 'EG',
  Guam = 'GU',
  Bahamas = 'BS',
  WesternSahara = 'EH',
  BosniaAndHerzegovina = 'BA',
  Monaco = 'MC',
  Singapore = 'SG',
  Cambodia = 'KH',
  ElSalvador = 'SV',
  Liechtenstein = 'LI',
  BritishIndianOceanTerritory = 'IO',
  Fiji = 'FJ',
  China = 'CN',
  ChristmasIsland = 'CX',
  Bolivia = 'BO',
  Greenland = 'GL',
  FaroeIslands = 'FO',
  Palestine = 'PS',
  Indonesia = 'ID',
  Aruba = 'AW',
  Benin = 'BJ',
  Bermuda = 'BM',
  PapuaNewGuinea = 'PG',
  Seychelles = 'SC',
  SouthGeorgiaAndTheSouthSandwichIslands = 'GS',
  Vietnam = 'VN',
  Taiwan = 'TW',
  Ecuador = 'EC',
  SanMarino = 'SM',
  Samoa = 'WS',
  Guatemala = 'GT',
  HolySee = 'VA',
  LaoPeoplesDemocraticRepublic = 'LA',
  DominicanRepublic = 'DO',
  CookIslands = 'CK',
  Tonga = 'TO',
  Guernsey = 'GG',
  Vanuatu = 'VU',
  Belize = 'BZ',
  Montenegro = 'ME',
  Oman = 'OM',
  SaintKittsAndNevis = 'KN',
  Niger = 'NE',
  Gabon = 'GA',
  Swaziland = 'SZ',
  Ghana = 'GH',
  Dominica = 'DM',
  Burundi = 'BI',
  BouvetIsland = 'BV',
  Togo = 'TG',
  NorthernMarianaIslands = 'MP',
  Tokelau = 'TK',
  Suriname = 'SR',
  SaintLucia = 'LC',
  SriLanka = 'LK',
  Zambia = 'ZM',
  Paraguay = 'PY',
  Botswana = 'BW',
  SaintMartin = 'MF',
  Djibouti = 'DJ',
  IsleOfMan = 'IM',
  SaintPierreAndMiquelon = 'PM',
  SolomonIslands = 'SB',
  FalklandIslands = 'FK',
  Senegal = 'SN',
  Cameroon = 'CM',
  CaboVerde = 'CV',
  Kenya = 'KE',
  Kyrgyzstan = 'KG',
  Anguilla = 'AI',
  Tuvalu = 'TV',
  CocosIslands = 'CC',
  AntiguaAndBarbuda = 'AG',
  Guinea = 'GN',
  BruneiDarussalam = 'BN',
  Bonaire = 'BQ',
  Tajikistan = 'TJ',
  SaoTomeAndPrincipe = 'ST',
  CoteDIvoire = 'CI',
  Madagascar = 'MG',
  HeardIslandAndMcDonaldIslands = 'HM',
  Montserrat = 'MS',
  Mozambique = 'MZ',
  Nepal = 'NP',
  Guyana = 'GY',
  GuineaBissau = 'GW',
  Nauru = 'NR',
  TurksAndCaicosIslands = 'TC',
  Bhutan = 'BT',
  Namibia = 'NA',
  VirginIslands = 'VI',
  Kiribati = 'KI',
  Malawi = 'MW',
  EquatorialGuinea = 'GQ',
  SaintHelena = 'SH',
  SaintBarthelemy = 'BL',
  Pitcairn = 'PN',
  UnitedStatesMinorOutlyingIslands = 'UM',
  Palau = 'PW',
  Gambia = 'GM',
  Niue = 'NU',
  Maldives = 'MV',
  Macao = 'MO',
  Turkmenistan = 'TM',
  Grenada = 'GD',
  Micronesia = 'FM',
  TimorLeste = 'TL',
  SintMaarten = 'SX',
  Mauritania = 'MR',
  MarshallIslands = 'MH',
  Comoros = 'KM',
  Algeria = 'DZ',
  Armenia = 'AM',
  Philippines = 'PH',
  SvalbardAndJanMayen = 'SJ',
  AmericanSamoa = 'AS',
  SaudiArabia = 'SA',
  Sweden = 'SE',
  Iceland = 'IS',
  SouthKorea = 'KR',
  Qatar = 'QA',
  Morocco = 'MA',
  CostaRica = 'CR',
  Uzbekistan = 'UZ',
  Venezuela = 'VE',
  Colombia = 'CO',
  MayotteStMartin = 'YT',
  Argentina = 'AR',
  Uruguay = 'UY',
  Chile = 'CL',
  Mexico = 'MX',
  PuertoRico = 'PR',
  Peru = 'PE',
  SouthAfrica = 'ZA',
  FrenchSouthernTerritories = 'TF',
  Kazakhstan = 'KZ',
  Kuwait = 'KW',
  Lesotho = 'LS',
  Luxembourg = 'LU',
  Malta = 'MT',
  SaintVincentAndTheGrenadines = 'VC',
  UnitedArabEmirates = 'AE',
}

type TRevertedCountry = { [key in Country]: string };

export const RevertedCountry: TRevertedCountry = Object.keys(Country).reduce(
  (acc, key) => {
    const countryKey = Country[key as keyof typeof Country];
    acc[countryKey] = key;
    return acc;
  },
  {} as TRevertedCountry
);

export const NOT_ALLOWED_COUNTRIES = new Set<string>([
  Country.Afghanistan,
  Country.AlandIslands,
  Country.Albania,
  Country.Algeria,
  Country.AmericanSamoa,
  Country.Andorra,
  Country.Angola,
  Country.Anguilla,
  Country.Antarctica,
  Country.AntiguaAndBarbuda,
  Country.Armenia,
  Country.Aruba,
  Country.Azerbaijan,
  Country.Bahamas,
  Country.Bahrain,
  Country.Bangladesh,
  Country.Barbados,
  Country.Belarus,
  Country.Belgium,
  Country.Belize,
  Country.Benin,
  Country.Bermuda,
  Country.Bhutan,
  Country.Bolivia,
  Country.Bonaire,
  Country.BosniaAndHerzegovina,
  Country.Botswana,
  Country.BouvetIsland,
  Country.Brazil,
  Country.BritishIndianOceanTerritory,
  Country.BruneiDarussalam,
  Country.Bulgaria,
  Country.BurkinaFaso,
  Country.Burundi,
  Country.CaboVerde,
  Country.Cambodia,
  Country.Cameroon,
  Country.CaymanIslands,
  Country.CentralAfricanRepublic,
  Country.Chad,
  Country.China,
  Country.ChristmasIsland,
  Country.CocosIslands,
  Country.Colombia,
  Country.Comoros,
  Country.CookIslands,
  Country.CostaRica,
  Country.CoteDIvoire,
  Country.Croatia,
  Country.Cuba,
  Country.Curacao,
  Country.CzechRepublic,
  Country.Cyprus,
  Country.DemocraticRepublicOfTheCongo,
  Country.Djibouti,
  Country.Dominica,
  Country.DominicanRepublic,
  Country.Estonia,
  Country.Ecuador,
  Country.Egypt,
  Country.ElSalvador,
  Country.EquatorialGuinea,
  Country.Eritrea,
  Country.Ethiopia,
  Country.FalklandIslands,
  Country.FaroeIslands,
  Country.Fiji,
  Country.FrenchGuiana,
  Country.FrenchPolynesia,
  Country.Gabon,
  Country.Gambia,
  Country.Georgia,
  Country.Ghana,
  Country.Gibraltar,
  Country.Greece,
  Country.Greenland,
  Country.Grenada,
  Country.Guadeloupe,
  Country.Guam,
  Country.Guatemala,
  Country.Guernsey,
  Country.Guinea,
  Country.GuineaBissau,
  Country.Guyana,
  Country.Haiti,
  Country.HeardIslandAndMcDonaldIslands,
  Country.HolySee,
  Country.Honduras,
  Country.HongKong,
  Country.Hungary,
  Country.Iceland,
  Country.India,
  Country.Indonesia,
  Country.Iran,
  Country.Iraq,
  Country.IsleOfMan,
  Country.Israel,
  Country.Jamaica,
  Country.Japan,
  Country.Jersey,
  Country.Jordan,
  Country.Kenya,
  Country.Kiribati,
  Country.Kyrgyzstan,
  Country.LaoPeoplesDemocraticRepublic,
  Country.Latvia,
  Country.Lebanon,
  Country.Liberia,
  Country.Libya,
  Country.Liechtenstein,
  Country.Lithuania,
  Country.Malta,
  Country.Macao,
  Country.Macedonia,
  Country.Madagascar,
  Country.Malawi,
  Country.Malaysia,
  Country.Maldives,
  Country.Mali,
  Country.MarshallIslands,
  Country.Martinique,
  Country.Mauritania,
  Country.Mauritius,
  Country.MayotteStMartin,
  Country.Micronesia,
  Country.Moldova,
  Country.Monaco,
  Country.Mongolia,
  Country.Montenegro,
  Country.Montserrat,
  Country.Morocco,
  Country.Mozambique,
  Country.Myanmar,
  Country.Namibia,
  Country.Nauru,
  Country.Nepal,
  Country.Netherlands,
  Country.NewCaledonia,
  Country.Nicaragua,
  Country.Niger,
  Country.Nigeria,
  Country.Niue,
  Country.NorfolkIsland,
  Country.NorthernMarianaIslands,
  Country.NorthKorea,
  Country.Oman,
  Country.Pakistan,
  Country.Palau,
  Country.Palestine,
  Country.Panama,
  Country.PapuaNewGuinea,
  Country.Paraguay,
  Country.Philippines,
  Country.Pitcairn,
  Country.Poland,
  Country.Qatar,
  Country.RepublicOfTheCongo,
  Country.Reunion,
  Country.Romania,
  Country.Russia,
  Country.Rwanda,
  Country.SaintBarthelemy,
  Country.SaintHelena,
  Country.SaintKittsAndNevis,
  Country.SaintLucia,
  Country.SaintMartin,
  Country.SaintPierreAndMiquelon,
  Country.Samoa,
  Country.SanMarino,
  Country.SaoTomeAndPrincipe,
  Country.SaudiArabia,
  Country.Senegal,
  Country.Serbia,
  Country.Seychelles,
  Country.SierraLeone,
  Country.Singapore,
  Country.SintMaarten,
  Country.Slovakia,
  Country.SolomonIslands,
  Country.Somalia,
  Country.SouthGeorgiaAndTheSouthSandwichIslands,
  Country.SouthKorea,
  Country.SouthSudan,
  Country.SriLanka,
  Country.Sudan,
  Country.Suriname,
  Country.SvalbardAndJanMayen,
  Country.Swaziland,
  Country.Sweden,
  Country.Syria,
  Country.Taiwan,
  Country.Tajikistan,
  Country.Tanzania,
  Country.Thailand,
  Country.TimorLeste,
  Country.Togo,
  Country.Tokelau,
  Country.Tonga,
  Country.TrinidadAndTobago,
  Country.Tunisia,
  Country.Turkey,
  Country.Turkmenistan,
  Country.TurksAndCaicosIslands,
  Country.Tuvalu,
  Country.Uganda,
  Country.Ukraine,
  Country.UnitedKingdom,
  Country.UnitedStates,
  Country.UnitedStatesMinorOutlyingIslands,
  Country.Uzbekistan,
  Country.Vanuatu,
  Country.Venezuela,
  Country.Vietnam,
  Country.VirginIslands,
  Country.WallisAndFutuna,
  Country.WesternSahara,
  Country.Yemen,
  Country.Zambia,
  Country.Zimbabwe,
]);

export enum AppParameter {
  NotificationCenter = 'notificationCenter',
  PromoPage = 'promoPage',
  NativeClick = 'nativeClick',
}

export const setAppCookie = ({
  name,
  ...rest
}: Omit<UseCookieProps, 'value' | 'days'>): UseCookieProps => ({
  name,
  value: { type: CookieType.String, data: timestampByUTC().toString() },
  days: 90,
  withReplace: true,
  ...rest,
});

export const STAG_PARAMS = [
  'stag',
  'ga',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'referrer',
  'ref_code',
];

export const crossSellStags = ['128825', '131911'];

export type RangeIP = [from: string, to: string];

const rangeIP = (from: string, to: string): RangeIP => [from, to];

export const blackListIPs: RangeIP[] = [
  rangeIP('195.37.175.16', '195.37.175.23'),
  rangeIP('195.37.175.16', '195.37.175.23'),
  rangeIP('176.94.185.176', '176.94.185.183'),
  rangeIP('195.243.21.200', '195.243.21.207'),
  rangeIP('178.13.67.160', '178.13.67.167'),
  rangeIP('195.243.128.32', '195.243.128.47'),
  rangeIP('213.128.119.112', '213.128.119.115'),
  rangeIP('194.196.76.0', '194.196.76.255'),
  rangeIP('213.139.133.144', '213.139.133.159'),
  rangeIP('193.175.51.0', '193.175.51.255'),
  rangeIP('176.94.185.48', '176.94.185.55'),
  rangeIP('213.211.242.224', '213.211.242.231'),
  rangeIP('217.239.183.0', '217.239.183.7'),
  rangeIP('217.239.232.88', '217.239.232.95'),
  rangeIP('213.139.136.16', '213.139.136.31'),
  rangeIP('217.239.228.48', '217.239.228.55'),
  rangeIP('217.239.175.208', '217.239.175.215'),
  rangeIP('213.211.236.128', '213.211.236.135'),
  rangeIP('213.211.237.32', '213.211.237.47'),
  rangeIP('217.239.161.248', '217.239.161.255'),
  rangeIP('217.239.179.240', '217.239.179.247'),
  rangeIP('217.89.43.24', '217.89.43.31'),
  rangeIP('62.153.239.96', '62.153.239.111'),
  rangeIP('62.153.249.208', '62.153.249.223'),
  rangeIP('217.239.241.104', '217.239.241.111'),
  rangeIP('217.89.190.208', '217.89.190.215'),
  rangeIP('62.153.192.0', '62.153.192.7'),
  rangeIP('217.239.233.88', '217.239.233.95'),
  rangeIP('62.153.119.212', '62.153.119.215'),
  rangeIP('62.154.181.224', '62.154.181.239'),
  rangeIP('217.89.66.8', '217.89.66.15'),
  rangeIP('62.157.120.160', '62.157.120.167'),
  rangeIP('62.159.151.192', '62.159.151.207'),
  rangeIP('62.27.36.0', '62.27.36.3'),
  rangeIP('62.157.237.176', '62.157.237.191'),
  rangeIP('62.157.241.32', '62.157.241.39'),
  rangeIP('80.149.210.152', '80.149.210.159'),
  rangeIP('62.225.102.128', '62.225.102.143'),
  rangeIP('80.146.161.0', '80.146.161.31'),
  rangeIP('80.154.205.16', '80.154.205.23'),
  rangeIP('80.155.151.168', '80.155.151.175'),
  rangeIP('87.129.146.200', '87.129.146.207'),
  rangeIP('81.14.181.64', '81.14.181.71'),
  rangeIP('80.155.17.88', '80.155.17.95'),
  rangeIP('81.14.186.80', '81.14.186.95'),
  rangeIP('82.195.76.120', '82.195.76.127'),
  rangeIP('82.195.68.0', '82.195.69.255'),
  rangeIP('80.155.47.200', '80.155.47.207'),
  rangeIP('87.129.136.64', '87.129.136.79'),
  rangeIP('85.126.148.176', '85.126.148.183'),
  rangeIP('82.195.76.192', '82.195.76.223'),
  rangeIP('87.129.216.96', '87.129.216.127'),
  rangeIP('87.129.199.96', '87.129.199.103'),
  rangeIP('87.129.31.8', '87.129.31.15'),
  rangeIP('87.129.41.200', '87.129.41.207'),
  rangeIP('87.129.180.248', '87.129.180.255'),
  rangeIP('87.129.25.8', '87.129.25.15'),
  rangeIP('87.129.33.40', '87.129.33.47'),
  rangeIP('87.129.149.72', '87.129.149.79'),
  rangeIP('87.129.222.200', '87.129.222.207'),
  rangeIP('87.129.4.0', '87.129.4.7'),
  rangeIP('87.129.63.208', '87.129.63.215'),
  rangeIP('87.129.59.56', '87.129.59.63'),
  rangeIP('87.130.104.0', '87.130.104.15'),
  rangeIP('87.130.1.168', '87.130.1.175'),
  rangeIP('87.130.126.16', '87.130.126.31'),
  rangeIP('87.130.1.176', '87.130.1.183'),
  rangeIP('87.130.127.0', '87.130.127.7'),
  rangeIP('87.130.1.216', '87.130.1.223'),
  rangeIP('87.129.5.0', '87.129.5.7'),
  rangeIP('87.129.59.8', '87.129.59.15'),
  rangeIP('87.190.33.48', '87.190.33.55'),
  rangeIP('91.25.132.232', '91.25.132.239'),
  rangeIP('87.130.62.120', '87.130.62.127'),
  rangeIP('87.130.73.48', '87.130.73.55'),
  rangeIP('87.190.50.104', '87.190.50.111'),
  rangeIP('88.79.215.192', '88.79.215.199'),
  rangeIP('87.130.34.40', '87.130.34.47'),
  rangeIP('91.25.113.88', '91.25.113.95'),
  rangeIP('87.191.32.248', '87.191.32.255'),
  rangeIP('87.191.41.176', '87.191.41.183'),
  rangeIP('91.25.153.104', '91.25.153.111'),
  rangeIP('91.25.159.224', '91.25.159.231'),
  rangeIP('91.25.139.176', '91.25.139.183'),
  rangeIP('91.25.144.24', '91.25.144.31'),
  rangeIP('91.25.148.224', '91.25.148.231'),
  rangeIP('91.25.150.88', '91.25.150.95'),
  rangeIP('91.25.159.48', '91.25.159.55'),
  rangeIP('91.25.143.88', '91.25.143.95'),
  rangeIP('91.25.140.104', '91.25.140.111'),
  rangeIP('91.25.148.80', '91.25.148.87'),
  rangeIP('91.25.182.56', '91.25.182.63'),
  rangeIP('91.26.20.224', '91.26.20.231'),
  rangeIP('91.26.82.192', '91.26.82.199'),
  rangeIP('91.25.207.32', '91.25.207.39'),
  rangeIP('91.26.102.216', '91.26.102.223'),
  rangeIP('91.25.174.80', '91.25.174.87'),
  rangeIP('91.26.71.248', '91.26.71.255'),
  rangeIP('91.25.254.192', '91.25.254.199'),
  rangeIP('91.25.170.184', '91.25.170.191'),
  rangeIP('91.25.210.64', '91.25.210.71'),
  rangeIP('176.95.153.192', '176.95.153.199'),
  rangeIP('178.15.142.128', '178.15.142.143'),
  rangeIP('178.15.142.208', '178.15.142.223'),
  rangeIP('193.134.4.0', '193.134.4.255'),
  rangeIP('194.11.240.0', '194.11.241.255'),
  rangeIP('145.253.232.16', '145.253.232.31'),
  rangeIP('188.20.139.36', '188.20.139.39'),
  rangeIP('145.253.101.208', '145.253.101.223'),
  rangeIP('193.47.255.0', '193.47.255.255'),
  rangeIP('193.175.67.32', '193.175.67.47'),
  rangeIP('195.37.190.152', '195.37.190.187'),
  rangeIP('195.37.234.128', '195.37.234.135'),
  rangeIP('195.37.234.152', '195.37.234.159'),
  rangeIP('194.117.122.118', '194.117.122.118'),
  rangeIP('195.37.209.96', '195.37.209.99'),
  rangeIP('195.37.232.168', '195.37.232.171'),
  rangeIP('195.37.234.140', '195.37.234.143'),
  rangeIP('195.145.26.0', '195.145.26.15'),
  rangeIP('195.37.190.188', '195.37.190.191'),
  rangeIP('195.37.173.180', '195.37.173.183'),
  rangeIP('212.184.78.16', '212.184.78.23'),
  rangeIP('195.37.234.252', '195.37.234.255'),
  rangeIP('213.23.40.224', '213.23.40.231'),
  rangeIP('217.239.214.88', '217.239.214.95'),
  rangeIP('217.239.164.176', '217.239.164.183'),
  rangeIP('213.39.246.136', '213.39.246.143'),
  rangeIP('213.39.247.104', '213.39.247.111'),
  rangeIP('195.50.183.56', '195.50.183.63'),
  rangeIP('217.239.185.208', '217.239.185.223'),
  rangeIP('217.239.170.88', '217.239.170.95'),
  rangeIP('62.157.195.224', '62.157.195.239'),
  rangeIP('217.239.223.168', '217.239.223.175'),
  rangeIP('62.154.215.128', '62.154.215.143'),
  rangeIP('62.153.91.96', '62.153.91.111'),
  rangeIP('62.214.157.192', '62.214.157.199'),
  rangeIP('217.239.232.88', '217.239.232.95'),
  rangeIP('62.153.238.0', '62.153.238.15'),
  rangeIP('217.239.219.224', '217.239.219.231'),
  rangeIP('217.239.219.240', '217.239.219.247'),
  rangeIP('217.239.240.152', '217.239.240.159'),
  rangeIP('62.214.157.232', '62.214.157.239'),
  rangeIP('80.155.25.128', '80.155.25.135'),
  rangeIP('81.14.172.32', '81.14.172.47'),
  rangeIP('84.46.72.112', '84.46.72.119'),
  rangeIP('85.182.157.120', '85.182.157.127'),
  rangeIP('84.46.96.96', '84.46.96.111'),
  rangeIP('87.129.24.8', '87.129.24.15'),
  rangeIP('82.135.37.248', '82.135.37.255'),
  rangeIP('80.155.141.40', '80.155.141.47'),
  rangeIP('80.155.60.144', '80.155.60.151'),
  rangeIP('87.129.62.224', '87.129.62.231'),
  rangeIP('87.191.32.240', '87.191.32.247'),
  rangeIP('91.25.156.144', '91.25.156.151'),
  rangeIP('91.25.158.240', '91.25.158.247'),
  rangeIP('87.245.121.240', '87.245.121.247'),
  rangeIP('89.106.221.80', '89.106.221.87'),
  rangeIP('91.25.154.240', '91.25.154.247'),
  rangeIP('87.129.61.176', '87.129.61.183'),
  rangeIP('89.106.221.88', '89.106.221.95'),
  rangeIP('91.25.144.48', '91.25.144.55'),
  rangeIP('91.26.109.248', '91.26.109.255'),
  rangeIP('91.26.8.192', '91.26.8.207'),
  rangeIP('91.26.58.104', '91.26.58.111'),
  rangeIP('91.26.96.240', '91.26.96.247'),
  rangeIP('91.26.111.72', '91.26.111.79'),
  rangeIP('91.26.52.16', '91.26.52.23'),
  rangeIP('91.26.59.8', '91.26.59.15'),
  rangeIP('92.198.25.141', '92.198.25.141'),
  rangeIP('91.26.105.32', '91.26.105.39'),
  rangeIP('91.26.49.8', '91.26.49.15'),
  rangeIP('93.240.132.16', '93.240.132.31'),
  rangeIP('93.241.13.184', '93.241.13.191'),
];

export const BONUS_RESTRICTED_COUNTRIES = [
  Country.Afghanistan,
  Country.Albania,
  Country.Azerbaijan,
  Country.Bangladesh,
  Country.Belarus,
  Country.Bulgaria,
  Country.Croatia,
  Country.Cyprus,
  Country.Estonia,
  Country.Finland,
  Country.Georgia,
  Country.India,
  Country.Iran,
  Country.Iraq,
  Country.Israel,
  Country.Latvia,
  Country.Lithuania,
  Country.Malaysia,
  Country.Pakistan,
  Country.Macedonia,
  Country.Moldova,
  Country.Romania,
  Country.Serbia,
  Country.Turkey,
];

export const LIVESPINS_TENANT_ID = 'LEVELUP';
export const LIVESPINS_RESTRICTED = [Country.Australia, Country.Canada];

export enum RestrictedProviders {
  LIVESPIN = 'livespin',
  AUTOPAY = 'autopay',
}

export const TRUELAB_PROVIDER_ID = 'truelab' as const;

export type QuestPoint = { level: number; cp: number; prize: string };

export const MIN_AGE = 18;
